/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { GoodDate } from '@utils/datetime';
import { Activity } from '@models/time/activity';
import httpRequest from '@utils/http-request';

const API_BASE = import.meta.env.VITE_SERVER_ENDPOINT;

interface TagJSON {
    id: string;
    name: string;
    description: string;
    color: number;
    createdAt: number;
    updatedAt: number;
    deletedAt?: number;
}

export class Tag {
    id: string;
    name: string;
    description: string;
    color: number;

    createdAt: Date;
    updatedAt: Date;
    deletedAt?: Date;

    activities?: Activity[];

    constructor(
        id: string,
        name: string,
        description: string,
        color: number,
        createdAt: number,
        updatedAt: number,
        deletedAt?: number,
        activities?: Activity[]
    ) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.color = color;

        this.createdAt = GoodDate.fromEpochSeconds(createdAt).date;
        this.updatedAt = GoodDate.fromEpochSeconds(updatedAt).date;
        this.deletedAt = deletedAt ? GoodDate.fromEpochSeconds(deletedAt).date : undefined;

        this.activities = activities;
    }

    static fromJSON(json: TagJSON): Tag {
        return new Tag(
            json.id,
            json.name,
            json.description,
            json.color,
            json.createdAt,
            json.updatedAt,
            json.deletedAt
        );
    }

    static fromJSONList(json: unknown): Tag[] {
        if (!Array.isArray(json)) return [];
        return json.map((tag) => Tag.fromJSON(tag as TagJSON));
    }

    static template: Tag = new Tag('', '', '', 0, GoodDate.nowInEpochSeconds(), GoodDate.nowInEpochSeconds());

    static list = async (): Promise<Tag[]> => {
        const response = await httpRequest(`${API_BASE}/time/tags`, 'GET'),
            json = await response.json();

        if (response.status === 200) {
            return Tag.fromJSONList(json.data);
        } else {
            throw new Error(json.error);
        }
    };

    static get = async (id: string): Promise<Tag> => {
        const response = await httpRequest(`${API_BASE}/time/tags/${id}`, 'GET'),
            json = await response.json();

        if (response.status === 200) {
            return Tag.fromJSON(json.data);
        } else {
            throw new Error(json.error);
        }
    };

    static save = async (id: string, tag: Tag): Promise<Tag> => {
        // The API only parses Unix timestamps, so serialize the Date fields as
        // epoch seconds instead of JSON.stringify's ISO strings.
        const payload = {
            id,
            name: tag.name,
            description: tag.description,
            color: tag.color,
            createdAt: Math.floor((tag.createdAt?.getTime() ?? Date.now()) / 1000),
            updatedAt: Math.floor((tag.updatedAt?.getTime() ?? Date.now()) / 1000),
        };
        const response = await httpRequest(`${API_BASE}/time/tags/${id}`, 'PUT', payload),
            json = await response.json();

        if (response.status === 200) {
            return Tag.fromJSON(json.data);
        } else {
            throw new Error(json.error);
        }
    };

    static saveByActivity = async (id: string, tags: string[]): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/time/activities/${id}/tags`, 'PUT', {
            tagIds: tags,
        });

        if (response.status !== 200) {
            throw new Error((await response.json()).error);
        }
    };

    static delete = async (id: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/time/tags/${id}`, 'DELETE'),
            json = await response.json();

        if (response.status !== 200) {
            throw new Error(json.error);
        }
    };
}
